/* eslint-disable no-console */
import {MinusCircleFilled, PlusCircleFilled} from '@ant-design/icons';
import {Col, Form, Radio, Select, Checkbox} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState} from 'react';

import Loader from '../../../../../components/Loader/Loader';
import {CommonErrors} from '../../../../../constants/Common';
import userGroupTypeJson from '../../../../../services/modules/JSON/getUserGroup';
import getPermissionsApi from '../../../../../services/modules/UserManager/getPermissions';
import {sortBy} from '../../../../../shared/commonFunctions';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import {
  extractPrefixSet,
  filterPermissionsAndModules,
  returnSingleNewLabel,
  roleFilter
} from '../Constants/commonFunctions';
import getLabelsFromSelectedPermissions from '../Constants/getLabelFromPermission';

import {PostFormData} from './UserManager';

interface ValueDescription {
  value: string;
  description: string;
}

interface NestedObject {
  [key: string]: ValueDescription[] | NestedObject;
}

type PermissionsType = Record<string, NestedObject | ValueDescription[]>;

interface AddUserDrawerProps {
  currentStep?: number;
  onPostDataChange: (data: Partial<any>) => void;
  postFormData: PostFormData;
}

const EditUserDrawerForm: React.FC<AddUserDrawerProps> = ({
  currentStep,
  postFormData,
  onPostDataChange
}) => {
  const [allPermissions, setAllPermissions] = useState<PermissionsType>({});
  const [showPermissions, setShowPermissions] = useState<PermissionsType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(extractPrefixSet(postFormData.permissions ?? []));
  const {setFieldValue} = useFormInstance();
  const accessTypes = useJSONStore.getState().accessTypes;
  const roleTypes = useJSONStore.getState().roleTypes;
  const [filterAccessType, setFilterAccessType] = useState(
    accessTypes.filter((i) => {
      return roleTypes
        .find(
          (i) =>
            i.type === postFormData.selectedStackHolder &&
            i?.name === postFormData.roles
        )
        ?.accessType?.includes(i?.name);
    }) ?? []
  );

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    const currentPermissions = postFormData.permissions ?? [];
    const updatedPermissions = checked
      ? [...currentPermissions, permission]
      : currentPermissions.filter((item) => item !== permission);
    setFieldValue('Permissions', updatedPermissions);
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, updatedPermissions)
    );
    onPostDataChange({permissions: updatedPermissions});
  };

  const collectAllPaths = (
    data: NestedObject | ValueDescription[],
    basePath = ''
  ): string[] => {
    const paths: string[] = [];
    Object.entries(data).forEach(([key, value]) => {
      const currentPath = basePath ? `${basePath}:${key}` : key;
      if (Array.isArray(value)) {
        paths.push(currentPath);
      } else if (typeof value === 'object') {
        paths.push(...collectAllPaths(value, currentPath));
      }
    });

    return paths;
  };

  const handleSelectAll = (isChecked: boolean) => {
    const allPaths = collectAllPaths(showPermissions);
    updateAllPermissions(allPaths, isChecked);
    setExpandedSections(
      extractPrefixSet(updateAllPermissions(allPaths, isChecked))
    );
    setFieldValue('Permissions', allPaths);
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, allPaths)
    );
  };

  const handleSelectGroup = (
    groupPath: string,
    groupData: NestedObject,
    isChecked: boolean
  ) => {
    const groupPaths = collectAllPaths(groupData, groupPath);
    const updatedPermissions = updateAllPermissions(groupPaths, isChecked);
    setExpandedSections(extractPrefixSet(updatedPermissions));
    setFieldValue('Permissions', updatedPermissions);
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, updatedPermissions)
    );
  };

  const updateAllPermissions = (
    paths: string[],
    isChecked: boolean
  ): string[] => {
    const updatedPermissions = new Set(postFormData.permissions ?? []);
    paths.forEach((path) => {
      if (isChecked) {
        updatedPermissions.add(path);
      } else {
        updatedPermissions.delete(path);
      }
    });
    const permissionArray = Array.from(updatedPermissions);
    onPostDataChange({permissions: permissionArray});

    return permissionArray;
  };

  const areAllPermissionsSelected = (
    data: NestedObject | ValueDescription[],
    basePath = ''
  ): boolean => {
    const allPaths = collectAllPaths(data, basePath);

    return allPaths.every((path) => postFormData.permissions?.includes(path));
  };

  useEffect(() => {
    // Update form fields when postFormData changes (important for edit modal)
    if (postFormData.roles) {
      setFieldValue('Role', postFormData.roles);
    }
    if (postFormData.selectedAccess) {
      setFieldValue('Access', postFormData.selectedAccess);
    }
    if (postFormData.permissions) {
      setFieldValue('Permissions', postFormData.permissions);
    }
  }, [
    postFormData.roles,
    postFormData.selectedAccess,
    postFormData.permissions,
    setFieldValue
  ]);

  useEffect(() => {
    Promise.all([userGroupTypeJson()])
      .then(() => {
        // setUserGroupSelected(
        //   userGroupTypes.find((i) => {
        //     return i._id === postFormData.selectedUserGroup;
        //   })
        // );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setShowPermissions(
      filterPermissionsAndModules(allPermissions, postFormData?.roles ?? '')
    );
  }, [postFormData.roles]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([getPermissionsApi(postFormData.selectedStackHolder)])
      .then(([permissions]) => {
        setAllPermissions(permissions);
        setShowPermissions(
          filterPermissionsAndModules(permissions, postFormData?.roles ?? '')
        );
        if (postFormData.permissions && postFormData.permissions?.length > 0) {
          setFieldValue(
            'selectedPermissions',
            getLabelsFromSelectedPermissions(
              permissions,
              postFormData.permissions
            )
          );
        }
      })
      .catch(() => {
        setAllPermissions({});
      })
      .finally(() => {
        setIsLoading(false);
      });
    onPostDataChange({selectedAdmin: ''});
  }, [postFormData.selectedStackHolder, postFormData.selectedUserGroup]);

  const renderPermissionItem = (
    description: string,
    key: string,
    path: string
  ) => (
    <div
      key={key}
      style={{
        // display: 'flex',
        // justifyContent: 'space-between',
        // padding: '5px 10px',
        border: '1px solid #D2D4DA',
        borderRadius: '10px',
        marginBottom: '10px'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '5px 10px',
          // border: '1px solid #D2D4DA',
          borderRadius: '5px'
        }}
      >
        <p>{returnSingleNewLabel(description, true)}</p>
        <Checkbox
          checked={postFormData.permissions?.includes(path)}
          onChange={(e) => {
            handlePermissionChange(path, e.target.checked);
          }}
        />
      </div>
    </div>
  );

  const renderPermissions = (
    data: NestedObject | ValueDescription[],
    path = ''
  ) => {
    return (
      <div>
        {path === '' && (
          <div style={{marginBottom: '10px'}}>
            <Checkbox
              checked={areAllPermissionsSelected(data)}
              onChange={(e) => {
                handleSelectAll(e.target.checked);
              }}
            >
              Select All Permissions
            </Checkbox>
          </div>
        )}
        {Object.entries(data).map(([key, value]) => {
          const currentPath = path ? `${path}:${key}` : key;
          const isExpanded = expandedSections[currentPath] ?? false;
          if (Array.isArray(value) && value.length > 0) {
            return renderPermissionItem(value[0].description, key, currentPath);
          }
          if (typeof value === 'object') {
            return (
              <div
                key={currentPath}
                style={{
                  border: '1px solid #D2D4DA',
                  borderRadius: '10px',
                  marginBottom: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px'
                  }}
                >
                  <div
                    style={{display: 'flex', alignItems: 'center', gap: '10px'}}
                  >
                    <Checkbox
                      checked={areAllPermissionsSelected(value, currentPath)}
                      onChange={(e) => {
                        handleSelectGroup(currentPath, value, e.target.checked);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                    {returnSingleNewLabel(key)}
                  </div>
                  <span
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={() => {
                      toggleSection(currentPath);
                    }}
                  >
                    {isExpanded ? (
                      <MinusCircleFilled
                        style={{
                          color: '#435B37',
                          fontSize: '20px',
                          marginLeft: '10px'
                        }}
                      />
                    ) : (
                      <PlusCircleFilled
                        style={{
                          color: '#435B37',
                          fontSize: '20px',
                          marginLeft: '10px'
                        }}
                      />
                    )}
                  </span>
                </div>
                {isExpanded && (
                  <div style={{paddingLeft: '16px', paddingRight: '16px'}}>
                    {renderPermissions(value, currentPath)}
                  </div>
                )}
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  if (isLoading) return <Loader />;
  if (currentStep === 0)
    return (
      <>
        <Col className="gutter-row">
          <Form.Item
            name="Role"
            label="Role"
            preserve={false}
            labelCol={{span: 24}}
            rules={[
              {required: true, message: CommonErrors.mandatory('Role Type')}
            ]}
            initialValue={postFormData.roles}
          >
            <Select
              allowClear
              showSearch
              className="select"
              placeholder="Select Role"
              value={postFormData.roles ?? ''}
              disabled={postFormData.selectedStackHolder === 'External'}
              options={sortBy(
                roleTypes
                  .filter((role) => {
                    const startingRole = postFormData.roles?.split(' ')?.[0];

                    return postFormData.selectedStackHolder === 'External'
                      ? role.type === postFormData.selectedStackHolder &&
                          role.name.startsWith(`${startingRole}`) &&
                          !role.name.includes('Admin')
                      : role.type === postFormData.selectedStackHolder &&
                          roleFilter().includes(role.name);
                  })
                  .map((role) => ({
                    label: role.name,
                    value: role.name,
                    accessType: role.accessType
                  })),
                'label',
                'asc'
              )}
              onChange={(value, record: any) => {
                onPostDataChange({roles: value, Role: value, permissions: []});
                setFieldValue('Access', null);
                setFieldValue(
                  'selectedPermissions',
                  getLabelsFromSelectedPermissions(
                    allPermissions,
                    postFormData.permissions
                  )
                );
                setFilterAccessType(
                  accessTypes.filter((i) => {
                    return record?.accessType?.includes(i?.name);
                  })
                );
              }}
              filterOption={(input, option) =>
                option?.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row">
          <Form.Item
            name="Access"
            label="Access"
            labelCol={{span: 24}}
            initialValue={postFormData.selectedAccess}
            rules={[
              {required: true, message: CommonErrors.mandatory('Access Type')}
            ]}
            preserve={false}
          >
            <Radio.Group
              value={postFormData.selectedAccess ?? null}
              onChange={(e) => {
                onPostDataChange({
                  selectedAccess: e.target.value,
                  Access: e.target.value
                });
                setFieldValue(
                  'selectedPermissions',
                  getLabelsFromSelectedPermissions(
                    allPermissions,
                    postFormData.permissions
                  )
                );
              }}
              style={{display: 'flex', justifyContent: 'start'}}
            >
              {filterAccessType.map((accessType) => (
                <Radio
                  key={accessType.description}
                  value={accessType.description}
                >
                  {accessType.description}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
        {postFormData.roles && (
          <Col className="gutter-row">
            <Form.Item
              name="Permissions"
              label={
                <span
                  style={{fontSize: '16px', fontWeight: 600, marginTop: '10px'}}
                >
                  Choose Modules & Permissions
                </span>
              }
              className="permissionsUM"
              labelCol={{span: 24}}
              initialValue={postFormData.permissions}
              validateTrigger="onChange"
              rules={[
                {
                  required: true,
                  validator: async () => {
                    if (
                      postFormData.permissions &&
                      postFormData.permissions.length > 0
                    ) {
                      await Promise.resolve();

                      return;
                    }

                    return await Promise.reject(
                      new Error('Please select at least one permission.')
                    );
                  }
                }
              ]}
            />
            <div>{allPermissions && renderPermissions(showPermissions)}</div>
          </Col>
        )}
        <Form.Item name="selectedPermissions" noStyle />
      </>
    );

  return null;
};

export default EditUserDrawerForm;
